import { useState } from 'react';
import { AuthProvider, useAuth }     from './contexts/AuthContext';
import { AppProvider, useApp }       from './contexts/AppContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';

import Sidebar from './components/layout/Sidebar';
import Header  from './components/layout/Header';
import Toast   from './components/ui/Toast';

import LoginPage       from './components/pages/LoginPage';
import Dashboard       from './components/pages/Dashboard';
import AssetsPage      from './components/pages/AssetsPage';
import FacilitiesPage  from './components/pages/FacilitiesPage';
import CheckoutPage    from './components/pages/CheckoutPage';
import RequestsPage    from './components/pages/RequestsPage';
import DeliveriesPage  from './components/pages/DeliveriesPage';
import TrackingPage    from './components/pages/TrackingPage';
import ReportsPage     from './components/pages/ReportsPage';
import UsersPage       from './components/pages/UsersPage';
import SettingsPage    from './components/pages/SettingsPage';
import SuperAdminPage  from './components/pages/SuperAdminPage';

function Spinner() {
  return (
    <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, background:'#060b10' }}>
      <div style={{ width:36, height:36, border:'3px solid #1a2a3a', borderTopColor:'#00e5a0', borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <div style={{ fontSize:13, color:'#3a5060', fontFamily:"'IBM Plex Mono',monospace" }}>Loading WMS…</div>
    </div>
  );
}

function SuperAdminShell() {
  const { signOut, profile } = useAuth();
  const platformName = localStorage.getItem('rfid_wms_platform_name') || 'RFID WMS';
  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background:'#060b10' }}>
      <div style={{ width:200, flexShrink:0, background:'#0a0f16', borderRight:'1px solid #1a2a3a', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'24px 18px 20px', borderBottom:'1px solid #1a2a3a' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:'#e8edf2' }}>{platformName}</div>
          <div style={{ fontSize:10, color:'#e05757', marginTop:3, fontWeight:700 }}>🛡 SUPERADMIN</div>
        </div>
        <div style={{ flex:1 }}/>
        <div style={{ padding:14, borderTop:'1px solid #1a2a3a' }}>
          <div style={{ fontSize:12, color:'#607080', marginBottom:8 }}>{profile?.name || profile?.email}</div>
          <button onClick={signOut} style={{ width:'100%', padding:'7px 0', borderRadius:7, background:'#e0575712', border:'1px solid #e0575730', color:'#e05757', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Sign Out</button>
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:28 }}>
        <SuperAdminPage />
      </div>
    </div>
  );
}

function AccountManagerShell() {
  const { signOut, profile } = useAuth();
  const platformName = localStorage.getItem('rfid_wms_platform_name') || 'RFID WMS';
  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background:'#060b10' }}>
      <div style={{ width:200, flexShrink:0, background:'#0a0f16', borderRight:'1px solid #1a2a3a', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'24px 18px 20px', borderBottom:'1px solid #1a2a3a' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:'#e8edf2' }}>{platformName}</div>
          <div style={{ fontSize:10, color:'#f5a623', marginTop:3, fontWeight:700 }}>💼 ACCOUNT MANAGER</div>
        </div>
        <div style={{ flex:1 }}/>
        <div style={{ padding:14, borderTop:'1px solid #1a2a3a' }}>
          <div style={{ fontSize:11, color:'#607080', marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{profile?.name || ''}</div>
          <div style={{ fontSize:10, color:'#3a5060', marginBottom:8, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{profile?.email}</div>
          <button onClick={signOut} style={{ width:'100%', padding:'7px 0', borderRadius:7, background:'#e0575712', border:'1px solid #e0575730', color:'#e05757', fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Sign Out</button>
        </div>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:28 }}>
        <SuperAdminPage accountManagerMode currentUserId={profile?.id} />
      </div>
    </div>
  );
}

function TenantApp() {
  const { loading:appLoading, toast, perms, requests, checkouts, deliveries } = useApp();
  const { loading:tenantLoading, limits } = useTenant();
  const [page, setPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (appLoading || tenantLoading) return <Spinner />;

  if (limits?.org_status === 'suspended') {
    return (
      <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#060b10', flexDirection:'column', gap:16 }}>
        <div style={{ fontSize:48 }}>🔒</div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:'#e8edf2' }}>Account Suspended</div>
        <div style={{ fontSize:13, color:'#607080', maxWidth:360, textAlign:'center' }}>Your organization's account has been suspended. Contact support to reactivate.</div>
      </div>
    );
  }

  const pendingReqCount = requests.filter(r=>r.status==='Pending').length;
  const activeOutCount  = checkouts.filter(c=>c.status==='Out').length;
  const pendingDelCount = deliveries.filter(d=>d.status==='Pending').length;

  const navigateTo = (p) => { setPage(p); setSidebarOpen(false); };

  return (
    <div className="app-shell" style={{ display:'flex', height:'100vh', overflow:'hidden', background:'#060b10' }}>

      {/* Mobile overlay */}
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <Sidebar
        page={page}
        setPage={navigateTo}
        perms={perms}
        badges={{ requests:pendingReqCount, checkout:activeOutCount, deliveries:pendingDelCount }}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="main-content" style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        <Header page={page} onMenuClick={() => setSidebarOpen(o => !o)} />
        <main className="main-pad" style={{ flex:1, overflowY:'auto', padding:28 }}>
          {page==='dashboard'  && <Dashboard setPage={navigateTo}/>}
          {page==='assets'     && <AssetsPage/>}
          {page==='facilities' && <FacilitiesPage/>}
          {page==='checkout'   && <CheckoutPage/>}
          {page==='requests'   && <RequestsPage/>}
          {page==='deliveries' && <DeliveriesPage/>}
          {page==='tracking'   && <TrackingPage/>}
          {page==='reports'    && <ReportsPage/>}
          {page==='users'      && perms.canManageUsers && <UsersPage/>}
          {page==='settings'   && perms.canConfig      && <SettingsPage/>}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="mobile-nav">
        <div className="mobile-nav-inner">
          {[
            { id:'dashboard',  icon:'📊', label:'Home'      },
            { id:'assets',     icon:'📦', label:'Assets'    },
            { id:'checkout',   icon:'🔄', label:'Checkout', badge: activeOutCount  },
            { id:'requests',   icon:'📋', label:'Requests', badge: pendingReqCount },
            { id:'deliveries', icon:'🚚', label:'More',     badge: pendingDelCount },
          ].map(item => (
            <button key={item.id} className={`mobile-nav-btn ${page===item.id?'active':''}`} onClick={() => navigateTo(item.id)}>
              {item.badge > 0 && <span className="mobile-nav-badge">{item.badge}</span>}
              <span className="icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
          <button className="mobile-nav-btn" onClick={() => setSidebarOpen(true)}>
            <span className="icon">☰</span>
            <span>Menu</span>
          </button>
        </div>
      </nav>

      {toast && <Toast msg={toast.msg} type={toast.type}/>}
    </div>
  );
}

function InnerApp() {
  const { user, loading:authLoading } = useAuth();
  const { isSuperAdmin, superAdminRole, loading:tenantLoading } = useTenant();

  if (authLoading || tenantLoading) return <Spinner/>;
  if (!user) return <LoginPage/>;
  if (isSuperAdmin && superAdminRole === 'account_manager') return <AccountManagerShell/>;
  if (isSuperAdmin) return <SuperAdminShell/>;

  return (
    <AppProvider>
      <TenantApp/>
    </AppProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <InnerApp/>
      </TenantProvider>
    </AuthProvider>
  );
}
